// L'ESECUTORE DEL COPIONE e il GIUDIZIO (I-1218; disegno del KB `reports/2026-10-26-tavolo-guidato-carte-disegno.md` §3): calcola ogni stato con le regole del gioco a partire da `stato0`
// (o da `salto`), giudica una mossa, e tiene lo stato del tutorial come un riduttore PURO (nessun React, nessun tempo: il ritmo delle mosse di `dopo` lo da` lo strato).
// Il giudizio e` copiato e generalizzato da src/lib/solitario/tutorial.ts:303-317 @ cad51507af021388ceb3a5de34eb61259665077d (`confronta`): li` le mosse legali sono un elenco calcolato a parte,
// qui sono il predicato `Motore.legale`. Le transizioni (avanza, rivedi, errori, «Mostramelo») sono copiate da src/components/solitario/TutorialSolitario.tsx:252-305 @ cad51507, SENZA
// l'avanzamento da solo dopo il gesto giusto (`PAUSA_MS`, :33 e :264-268): era la causa del «lampo» (cantiere qsolitario-orcr-30-lampo; grammatica rev.1, dispatch-31 punto 1).
import { AVANTI } from './tipi';
import type { Copione, Giudizio, Motore, MossaDiGioco, Passo } from './tipi';

/** Le mosse giuste del passo: nessuna (lettura), una, o l'elenco delle equivalenti. */
export function mosseAttese<S, M extends MossaDiGioco, B>(passo: Passo<S, M, B>): readonly M[] {
  const attesa = passo.attesa;
  if (attesa === AVANTI) return [];
  return Array.isArray(attesa) ? (attesa as readonly M[]) : [attesa as M];
}

/**
 * Cio` che la mossa vale nel passo, nello stato `s`. Un passo di sola lettura non accetta mosse. La mossa attesa (o una equivalente) e` `giusta`; una legale diversa e` `altra` (non uno sbaglio
 * di regole: si mostra il perche` del passo); il resto e` `sbagliata`.
 */
export function giudica<S, V, M extends MossaDiGioco, B>(motore: Motore<S, V, M>, passo: Passo<S, M, B>, s: S, m: M): Giudizio {
  const attese = mosseAttese(passo);
  if (attese.length === 0) return 'sbagliata';
  if (attese.some((a) => motore.uguale(a, m))) return 'giusta';
  return motore.legale(s, m) ? 'altra' : 'sbagliata';
}

/** Lo stato con cui ogni passo PARTE: `salto` se c'e`, altrimenti quello del passo prima dopo la sua prima mossa attesa e le sue mosse di `dopo`. Calcolato una volta, dalle regole. */
export function statiDiPartenza<S, V, M extends MossaDiGioco, B>(motore: Motore<S, V, M>, copione: Copione<S, M, B>): S[] {
  const stati: S[] = [];
  let s = copione.stato0;
  for (const passo of copione.passi) {
    if (passo.salto !== undefined) s = passo.salto;
    stati.push(s);
    const prima = mosseAttese(passo)[0];
    if (prima !== undefined) s = motore.applica(s, prima);
    for (const d of passo.dopo ?? []) s = motore.applica(s, d);
  }
  return stati;
}

/**
 * Il controllo di un copione, per i test di chi lo scrive: ogni mossa attesa (anche ciascuna delle equivalenti) e` LEGALE nello stato in cui il passo parte, e ogni passo con una mossa
 * da fare ha cosa illuminare, e nessun passo ha `attesa: []` (senza uscita). Rende l'elenco dei problemi (vuoto = il copione e` onesto con le regole). Le mosse di `dopo` non si controllano: si applicano senza `legale` per scelta.
 */
export function problemiDelCopione<S, V, M extends MossaDiGioco, B>(motore: Motore<S, V, M>, copione: Copione<S, M, B>): string[] {
  const problemi: string[] = [];
  const stati = statiDiPartenza(motore, copione);
  copione.passi.forEach((passo, i) => {
    const attese = mosseAttese(passo);
    if (Array.isArray(passo.attesa) && attese.length === 0) problemi.push(`passo ${i + 1} (${passo.id}): \`attesa\` e\` un elenco vuoto: il passo non ha uscita (nessuna mossa e nessun «Avanti»)`);
    attese.forEach((m, j) => {
      if (!motore.legale(stati[i] as S, m)) problemi.push(`passo ${i + 1} (${passo.id}): la mossa attesa ${j + 1} non e\` legale`);
    });
    if (attese.length > 0 && passo.evidenzia.length === 0) problemi.push(`passo ${i + 1} (${passo.id}): una mossa da fare e niente da illuminare`);
  });
  return problemi;
}

export type Fase = 'apertura' | 'passi' | 'riepilogo';
/** Il RISCONTRO del passo, che il riquadro di stato mostra nello stesso posto e con la stessa altezza: in attesa, «Non lì», «anche questa si potrebbe, ma…», «Giusto!». */
export type Riscontro = 'attesa' | 'nonLi' | 'altra' | 'giusto';

export interface StatoTutorial<S, M> {
  fase: Fase;
  /** L'indice del passo (0 all'apertura). */
  indice: number;
  /** Lo stato del gioco in vista. */
  stato: S;
  riscontro: Riscontro;
  /** Le mosse non giuste nello stesso passo (sia `altra` sia `sbagliata`): alla seconda compare «Mostramelo». */
  errori: number;
  /** «Mostramelo» acceso. */
  mostra: boolean;
  /** Dopo una mossa `altra`: la chiave del suo perche` (`Passo.altra(m)`), se il passo ne da` una. */
  perche?: string;
  /** Le mosse di `dopo` non ancora applicate (dopo la giusta, o dopo il primo tocco su «Avanti» di una lettura, una per volta). */
  coda: readonly M[];
  /** Una lettura con `dopo`: il primo tocco su «Avanti» ha messo in vista la coda. Da qui in poi «Avanti» a coda vuota cambia passo. */
  dopoAvviato: boolean;
}

export type Evento<M> =
  | { tipo: 'inizia' }
  | { tipo: 'mossa'; mossa: M }
  /** Applica la prossima mossa di `dopo` (lo strato la manda a passo leggibile). */
  | { tipo: 'dopo' }
  | { tipo: 'avanti' }
  | { tipo: 'mostramelo' }
  | { tipo: 'rivedi' };

/** Dopo quanti errori nello stesso passo compare «Mostramelo» (il solitario: :528, «errori >= 2»). */
export const ERRORI_PER_MOSTRAMELO = 2;

/** «Avanti» sta nella riga d'azione: dalla giusta (e dall'inizio di un passo di sola lettura) fino al tocco, sempre lo STESSO nodo: non compare e non sparisce dopo (niente lampo). */
export function avantiInVista<S, M extends MossaDiGioco, B>(st: StatoTutorial<S, M>, passo: Passo<S, M, B> | undefined): boolean {
  return st.fase === 'passi' && passo !== undefined && (st.riscontro === 'giusto' || passo.attesa === AVANTI);
}

/** Si puo` toccare «Avanti»: quando e` in vista, e MAI con mosse di `dopo` ancora da mostrare (chi impara deve vedere chi ha giocato cosa, regola (b) del kit): finche` la coda non e` vuota resta disabilitato.
 *  Nella lettura con `dopo` il primo tocco non cambia passo: avvia la coda (v. `dopoDaAvviare`). */
export function puoAvanzare<S, M extends MossaDiGioco, B>(st: StatoTutorial<S, M>, passo: Passo<S, M, B> | undefined): boolean {
  return avantiInVista(st, passo) && st.coda.length === 0;
}

/** Una lettura con `dopo` il cui primo tocco su «Avanti» non e` ancora arrivato: quel tocco mette in vista la coda, NON cambia passo (il cambio e` sempre e solo un tocco, mai un tempo). */
export function dopoDaAvviare<S, M extends MossaDiGioco, B>(st: StatoTutorial<S, M>, passo: Passo<S, M, B> | undefined): boolean {
  return inLettura(st, passo) && (passo?.dopo?.length ?? 0) > 0 && !st.dopoAvviato;
}

/** Si aspetta una mossa del tavolo: il tavolo e` abilitato solo qui. */
export function aspettaMossa<S, M extends MossaDiGioco, B>(st: StatoTutorial<S, M>, passo: Passo<S, M, B> | undefined): boolean {
  return st.fase === 'passi' && passo !== undefined && passo.attesa !== AVANTI && st.riscontro !== 'giusto';
}

/** Un passo di SOLA LETTURA e` in corso: le sue evidenze si guardano, non si toccano (nessuna attesa di tocco, nessun giudizio, il tavolo non accetta mosse). */
export function inLettura<S, M extends MossaDiGioco, B>(st: StatoTutorial<S, M>, passo: Passo<S, M, B> | undefined): boolean {
  return st.fase === 'passi' && passo !== undefined && passo.attesa === AVANTI;
}

/** «Mostramelo» si puo` chiedere: dopo due errori, se il passo sa cosa mostrare, finche` la mossa non e` fatta. */
export function puoMostrare<S, M extends MossaDiGioco, B>(st: StatoTutorial<S, M>, passo: Passo<S, M, B> | undefined): boolean {
  return aspettaMossa(st, passo) && st.errori >= ERRORI_PER_MOSTRAMELO && passo?.mostra !== undefined;
}

/** L'esecutore di un copione: lo stato iniziale (l'apertura) e il riduttore. Gli stati di partenza dei passi si calcolano una volta. */
export function creaEsecutore<S, V, M extends MossaDiGioco, B>(motore: Motore<S, V, M>, copione: Copione<S, M, B>) {
  const partenze = statiDiPartenza(motore, copione);
  const n = copione.passi.length;
  const alPasso = (indice: number): StatoTutorial<S, M> => ({
    fase: 'passi',
    indice,
    stato: partenze[indice] as S,
    riscontro: 'attesa',
    errori: 0,
    mostra: false,
    coda: [],
    dopoAvviato: false,
    perche: undefined,
  });
  const iniziale = (): StatoTutorial<S, M> => ({ ...alPasso(0), fase: 'apertura' });

  function riduci(st: StatoTutorial<S, M>, ev: Evento<M>): StatoTutorial<S, M> {
    const passo = copione.passi[st.indice];
    switch (ev.tipo) {
      case 'inizia':
      case 'rivedi':
        return n === 0 ? { ...st, fase: 'riepilogo' } : alPasso(0);
      case 'mossa': {
        // dopo la giusta il tavolo non conta piu`; in un passo di sola lettura (il tavolo e` spento) una mossa che arriva lo stesso e` «Non lì»
        if (st.fase !== 'passi' || passo === undefined || st.riscontro === 'giusto') return st;
        const g = giudica(motore, passo, st.stato, ev.mossa);
        if (g === 'giusta') return { ...st, stato: motore.applica(st.stato, ev.mossa), riscontro: 'giusto', mostra: false, coda: passo.dopo ?? [], perche: undefined };
        // il messaggio resta FERMO finche` non arriva il gesto dopo: nessun tempo lo spegne
        if (g === 'altra') return { ...st, riscontro: 'altra', errori: st.errori + 1, perche: passo.altra?.(ev.mossa) };
        return { ...st, riscontro: 'nonLi', errori: st.errori + 1, perche: undefined };
      }
      case 'dopo': {
        const [prossima, ...resto] = st.coda;
        if (prossima === undefined) return st;
        // `dopo` si applica SENZA il controllo di `legale` (le mosse degli altri posti e le transizioni automatiche)
        return { ...st, stato: motore.applica(st.stato, prossima), coda: resto };
      }
      case 'avanti':
        if (!puoAvanzare(st, passo)) return st;
        // una lettura con `dopo`: il primo tocco mette in vista la coda (una mossa per volta, lo strato), il passo e` lo stesso; il cambio di passo e` il tocco dopo, a coda vuota
        if (dopoDaAvviare(st, passo)) return { ...st, dopoAvviato: true, coda: passo?.dopo ?? [] };
        // la coda di `dopo` e` vuota (lo richiede `puoAvanzare`): al riepilogo la posizione e` gia` quella finale
        return st.indice >= n - 1 ? { ...st, fase: 'riepilogo' } : alPasso(st.indice + 1);
      case 'mostramelo':
        return puoMostrare(st, passo) ? { ...st, mostra: true } : st;
    }
  }

  return { iniziale, riduci, partenze };
}
